import { motion } from 'framer-motion';
import { Sparkles, AlertTriangle, Lightbulb, Rocket } from 'lucide-react';
import { getElementsByBlock } from '../data/elements';
import ElementCard from './ElementCard';

const futureCategories = [
  {
    name: 'Emerging',
    description: 'Technologies in early deployment',
    elements: ['Nm', 'Bc'],
    color: '#4ECDC4',
  },
  {
    name: 'Research',
    description: 'Active research phase',
    elements: ['Si', 'Qm', 'Pm'],
    color: '#45B7D1',
  },
  {
    name: 'Theoretical',
    description: 'Conceptual frameworks',
    elements: ['Agi', 'Sa', 'Um'],
    color: '#96CEB4',
  },
  {
    name: 'Speculative',
    description: 'Highly uncertain',
    elements: ['Asi', 'Rc', 'Cn', 'Sg'],
    color: '#FFD700',
  },
];

export default function FutureBlock({ onElementClick }) {
  const futureElements = getElementsByBlock('future');

  return (
    <div className="max-w-6xl mx-auto px-4">
      {/* Section Header */}
      <motion.div
        className="text-center mb-12"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-future/10 border border-future/30 mb-6">
          <Sparkles className="w-4 h-4 text-future" />
          <span className="text-sm text-future">Speculative / Theoretical</span>
        </div>
        <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">
          The <span className="text-gradient-gold">Future Block</span>
        </h2>
        <p className="text-gray-400 max-w-2xl mx-auto">
          Elements that don't yet exist in mature form — concepts that are speculative, 
          theoretical, or early-research-phase. Subject to rapid redefinition.
        </p>
      </motion.div>

      {/* Future Block Image */}
      <motion.div
        className="mb-16"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <img
          src="/images/Future_Block_Speculative_Elements.png"
          alt="AI Future Block - Speculative Elements"
          className="w-full rounded-xl border border-future/30"
        />
      </motion.div>

      {/* Warning Banner */}
      <motion.div
        className="mb-12 p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/30 flex items-start gap-4"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <AlertTriangle className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="font-semibold text-yellow-500 mb-1">Provisional Elements</h3>
          <p className="text-sm text-gray-400">
            Elements in this block are subject to redefinition, combination, or removal as the field evolves. 
            They represent current thinking about future possibilities, not established facts.
          </p>
        </div>
      </motion.div>

      {/* Categories */}
      <div className="grid md:grid-cols-2 gap-6 mb-12">
        {futureCategories.map((category, index) => (
          <motion.div
            key={category.name}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1 }}
            className="p-6 rounded-xl bg-dark-800/50 border border-white/5"
          >
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: category.color }}
              />
              <h3 className="font-bold">{category.name}</h3>
            </div>
            <p className="text-sm text-gray-400 mb-4">{category.description}</p>
            <div className="flex flex-wrap gap-2">
              {futureElements
                .filter(el => category.elements.includes(el.symbol))
                .map(element => (
                  <ElementCard
                    key={element.symbol}
                    element={element}
                    onClick={onElementClick}
                    size="small"
                  />
                ))}
            </div>
          </motion.div>
        ))}
      </div>

      {/* AGI Debate Section */}
      <motion.div
        className="p-6 rounded-xl bg-gradient-to-br from-future/10 to-transparent border border-future/20"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <div className="flex items-center gap-3 mb-6">
          <Lightbulb className="w-6 h-6 text-future" />
          <h3 className="text-xl font-bold">The AGI Debate</h3>
        </div>
        
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="p-4 rounded-lg bg-dark-800/50">
              <h4 className="font-semibold text-green-400 mb-2">🚀 Near-term Optimists</h4>
              <p className="text-sm text-gray-400">
                AGI within 5-10 years. Current architectures just need scale. 
                Reasoning models are inflection points.
              </p>
            </div>
            <div className="p-4 rounded-lg bg-dark-800/50">
              <h4 className="font-semibold text-blue-400 mb-2">🔬 Gradual Progressives</h4>
              <p className="text-sm text-gray-400">
                AGI within 20-50 years. Fundamental breakthroughs still needed. 
                Will emerge from integrating multiple approaches.
              </p>
            </div>
          </div>
          <div className="space-y-4">
            <div className="p-4 rounded-lg bg-dark-800/50">
              <h4 className="font-semibold text-amber-400 mb-2">🤔 Skeptics</h4>
              <p className="text-sm text-gray-400">
                AGI may not be achievable. Digital computation might not replicate 
                cognition. "AGI" is poorly defined.
              </p>
            </div>
            <div className="p-4 rounded-lg bg-dark-800/50">
              <h4 className="font-semibold text-purple-400 mb-2">💡 Paradigm Shifters</h4>
              <p className="text-sm text-gray-400">
                AGI requires new approaches. Expect breakthroughs from neuroscience, 
                new mathematics, or unknown discoveries.
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
